import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, Target, Scale, Award, Users, Globe2, Quote, FileText, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell } from "@/components/PageShell";
import { SeoHead } from "@/components/SeoHead";
import { Reveal } from "@/components/Reveal";
import aboutHero from "@/assets/about-hero.jpg";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Us — Comm42 SA" },
      { name: "description", content: "Comm42 — international specialists in strategy, investigation, legal protection and private logistics worldwide." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://comm42.ch/about" },
      { property: "og:title", content: "About Us — Comm42 SA" },
      { property: "og:description", content: "Comm42 partners with executives and organizations worldwide for strategy, security and legal protection." },
      { property: "og:image", content: aboutHero },
      { property: "og:image:alt", content: "Comm42 — partnership and strategic collaboration" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Us — Comm42 SA" },
      { name: "twitter:description", content: "Let's build something great together — strategy, security and legal protection, worldwide." },
      { name: "twitter:image", content: aboutHero },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/about" },
    ],
  }),
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.toLowerCase().startsWith("it");
  const pdfHref = isItalian ? "/Comm42-Presentazione-IT.pdf" : "/Comm42-Who-we-are.pdf";
  const pdfTitle = isItalian ? "Comm42 — Presentazione aziendale" : "Comm42 — Who we are";
  const pdfEyebrow = isItalian ? "Profilo aziendale" : "Company Profile";
  const pdfDesc = isItalian
    ? "Scarica la nostra presentazione aziendale (PDF)."
    : "Download our company presentation (PDF).";
  const pdfCta = isItalian ? "Scarica PDF" : "Download PDF";
  const values = [
    { Icon: ShieldCheck, title: t("about.values.discretion.title"), desc: t("about.values.discretion.desc") },
    { Icon: Target, title: t("about.values.precision.title"), desc: t("about.values.precision.desc") },
    { Icon: Scale, title: t("about.values.integrity.title"), desc: t("about.values.integrity.desc") },
    { Icon: Award, title: t("about.values.results.title"), desc: t("about.values.results.desc") },
  ];
  const stats = [
    { value: "330", label: t("about.stats.projects") },
    { value: "230", label: t("about.stats.consultations") },
    { value: "260", label: t("about.stats.achievements") },
    { value: "135", label: t("about.stats.clients") },
  ];
  const testimonials = [
    { quote: t("about.testimonials.t1.quote"), name: "John B.", role: t("about.testimonials.t1.role") },
    { quote: t("about.testimonials.t2.quote"), name: "Kelly N.", role: t("about.testimonials.t2.role") },
    { quote: t("about.testimonials.t3.quote"), name: "M. W.", role: t("about.testimonials.t3.role") },
  ];
  return (
    <PageShell>
      <SeoHead page="about" />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-card pt-40 pb-28">
        <img
          src={aboutHero}
          alt="Comm42 partnership handshake"
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-card/70 via-card/60 to-card" />
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_30%_20%,oklch(0.6_0.15_240/0.5),transparent_50%),radial-gradient(circle_at_80%_80%,oklch(0.85_0.12_85/0.25),transparent_50%)]" />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <p className="reveal-up text-xs tracking-[0.4em] text-primary">{t("about.eyebrow")}</p>
          <h1 className="reveal-up mt-6 font-serif text-5xl md:text-6xl" style={{ animationDelay: "0.15s" }}>
            {t("about.title")}
          </h1>
          <p className="reveal-up mx-auto mt-6 max-w-2xl text-lg text-muted-foreground" style={{ animationDelay: "0.3s" }}>
            {t("about.subtitle")}
          </p>
          <div className="reveal-up mt-10 flex flex-wrap items-center justify-center gap-4" style={{ animationDelay: "0.45s" }}>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-foreground px-10 py-3 text-xs tracking-[0.3em] text-background transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-[0_20px_40px_-10px_oklch(0.5_0.09_235/0.4)]"
            >
              {t("about.contact")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="bg-background py-24">
        <div className="mx-auto grid max-w-6xl gap-16 px-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs tracking-[0.4em] text-primary">{t("about.aboutEyebrow")}</p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("about.aboutTitle")}</h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-lg text-muted-foreground">{t("about.aboutDesc")}</p>
          </Reveal>
        </div>
      </section>

      {/* Company presentation PDF */}
      <section className="bg-background pb-24">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="group relative flex flex-col items-start gap-6 overflow-hidden border border-border/40 bg-card/60 p-10 transition-colors hover:border-primary/60 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-5">
                <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center border border-primary/30 bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.4em] text-primary">{pdfEyebrow}</p>
                  <h3 className="mt-3 font-serif text-2xl">{pdfTitle}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{pdfDesc}</p>
                </div>
              </div>
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                className="group/btn inline-flex items-center gap-2 bg-foreground px-8 py-3 text-xs tracking-[0.3em] text-background transition-all hover:bg-primary hover:text-primary-foreground"
              >
                <Download className="h-3.5 w-3.5" /> {pdfCta}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission / Why / */}
      <section className="bg-card/30 py-24">
        <div className="mx-auto grid max-w-6xl gap-6 px-6 md:grid-cols-2">
          {[
            { Icon: Target, title: t("about.mission.title"), desc: t("about.mission.desc") },
            { Icon: Users, title: t("about.why.title"), desc: t("about.why.desc") },
          ].map(({ Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 120}>
              <div className="tilt-card group relative h-full overflow-hidden border border-border/40 bg-card/60 p-10 transition-colors hover:border-primary/60">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative inline-flex h-12 w-12 items-center justify-center border border-primary/30 bg-primary/10 transition-all duration-500 group-hover:rotate-6 group-hover:border-primary">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="relative mt-6 font-serif text-2xl">{title}</h3>
                <p className="relative mt-3 text-base text-muted-foreground">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.4em] text-primary">{t("about.valuesEyebrow")}</p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("about.valuesTitle")}</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map(({ Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 80}>
                <div className="group relative h-full border border-border/40 bg-card/40 p-8 transition-colors hover:border-primary/60">
                  <div className="inline-flex h-11 w-11 items-center justify-center border border-primary/30 bg-primary/10 transition-all duration-500 group-hover:rotate-6">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mt-5 font-serif text-xl">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-card/30 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.4em] text-primary">{t("about.testimonialsEyebrow")}</p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("about.testimonialsTitle")}</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {testimonials.map(({ quote, name, role }, i) => (
              <Reveal key={name} delay={i * 100}>
                <figure className="relative h-full border border-border/40 bg-card/60 p-8">
                  <Quote className="h-7 w-7 text-primary/60" />
                  <blockquote className="mt-4 text-sm leading-relaxed text-muted-foreground">{quote}</blockquote>
                  <figcaption className="mt-6 border-t border-border/40 pt-4">
                    <div className="font-serif text-lg">{name}</div>
                    <div className="text-xs tracking-[0.2em] text-primary">{role}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative overflow-hidden border-y border-border/40 bg-card py-24">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_50%,oklch(0.6_0.15_240/0.4),transparent_60%),radial-gradient(circle_at_85%_30%,oklch(0.85_0.12_85/0.25),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-6">
          <Reveal className="text-center">
            <p className="text-xs tracking-[0.4em] text-primary">{t("about.statsEyebrow")}</p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("about.statsTitle")}</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map(({ value, label }, i) => (
              <Reveal key={label} delay={i * 80} className="text-center">
                <div className="font-serif text-6xl text-primary">{value}</div>
                <div className="mt-2 text-xs tracking-[0.3em] text-muted-foreground">{label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-background py-24">
        <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
          <Globe2 className="mx-auto h-10 w-10 text-primary" />
          <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("about.ctaTitle")}</h2>
          <p className="mt-6 text-muted-foreground">{t("about.ctaDesc")}</p>
          <Link
            to="/contact"
            className="group mt-10 inline-flex items-center gap-2 bg-primary px-10 py-4 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_30px_60px_-15px_oklch(0.5_0.09_235/0.6)]"
          >
            {t("about.contact")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </PageShell>
  );
}
